/**
 * LCFPILanding.js — Public landing page for Lagos Climate Finance Pipeline Initiative
 * Fully public. Institutional design. Stats from live API.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, TrendingUp, Award, Shield, BarChart2, ArrowRight, CheckCircle2, ExternalLink } from "lucide-react";

const BTN_PRIMARY = "inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-bold px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors";
const BTN_OUTLINE = "inline-flex items-center gap-2 border border-slate-300 text-slate-700 text-sm font-bold px-6 py-3 rounded-lg hover:bg-slate-50 transition-colors";
const BTN_DARK = "inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white text-sm font-bold px-6 py-3 rounded-lg hover:bg-white/20 transition-colors border border-white/20";

const StatCard = ({value, label, sub}) => (
  <div className="text-center">
    <p className="text-4xl font-black text-white mb-1">{value}</p>
    <p className="text-sm font-semibold text-blue-200">{label}</p>
    {sub && <p className="text-xs text-blue-300 mt-0.5">{sub}</p>}
  </div>
);

const SectorCard = ({icon, name, desc, tags}) => (
  <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 hover:shadow-md transition-shadow">
    <span className="text-2xl mb-3 block">{icon}</span>
    <p className="text-sm font-bold text-slate-800 mb-1">{name}</p>
    <p className="text-xs text-slate-500 mb-3">{desc}</p>
    <div className="flex flex-wrap gap-1">
      {tags.map(t=><span key={t} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded font-medium">{t}</span>)}
    </div>
  </div>
);

const StepCard = ({num, title, desc, color}) => (
  <div className="flex gap-4">
    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black text-white flex-shrink-0 mt-0.5" style={{background:color}}>{num}</div>
    <div>
      <p className="text-sm font-bold text-slate-800 mb-1">{title}</p>
      <p className="text-xs text-slate-500">{desc}</p>
    </div>
  </div>
);

export default function LCFPILanding() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({total_projects:8,total_funding_usd:37950000,tier1_ready:3,sectors_covered:6,eco_labels_issued:5,total_tco2e_monitored:4820});
  const [statsLoaded, setStatsLoaded] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lcfpi/public/stats`)
      .then(r => r.json())
      .then(d => { if (d.total_projects) { setStats(d); setStatsLoaded(true); } })
      .catch(() => {});
  }, []);

  const fmtU = v => { if(!v) return "—"; const n=Number(v); if(n>=1e9) return `$${(n/1e9).toFixed(1)}B`; if(n>=1e6) return `$${(n/1e6).toFixed(1)}M`; return `$${n.toLocaleString()}`; };

  return (
    <div style={{ fontFamily:"'Inter','DM Sans',sans-serif", color:"#1e293b" }}>

      {/* NAV */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Globe size={16} color="white" />
            </div>
            <div>
              <p className="text-sm font-black text-slate-900">LCFPI</p>
              <p className="text-xs text-slate-400">Lagos Climate Finance Pipeline</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-xs font-medium text-slate-500">
            <a href="#how-it-works" className="hover:text-slate-700">How It Works</a>
            <a href="#sectors" className="hover:text-slate-700">Sectors</a>
            <a href="#eco-labels" className="hover:text-slate-700">Eco-Labels</a>
            <a href="#investors" className="hover:text-slate-700">Investors</a>
            <a href="#apply" className="hover:text-slate-700">Apply</a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/login")} className={BTN_OUTLINE} data-testid="nav-login">Sign In</button>
            <button onClick={() => navigate("/login?redirect=/lcfpi/submit")} className={BTN_PRIMARY} data-testid="nav-apply">Apply to Pipeline</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#1e40af] py-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-1.5 mb-8">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs font-semibold text-blue-200">Powered by DeCarbonIQ · OCCE Lagos State</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
            Lagos Climate Finance<br />
            <span className="text-blue-300">Pipeline Initiative</span>
          </h1>
          <p className="text-xl text-blue-200 mb-4 max-w-3xl mx-auto font-medium">
            Building Africa's largest verified climate and circular economy financing pipeline for SMEs, infrastructure projects, and institutional climate investments.
          </p>
          <p className="text-sm text-blue-400 mb-10">
            Powered by DeCarbonIQ in collaboration with the Office of Climate Change & Circular Economy (OCCE), Lagos State.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mb-14">
            <button onClick={() => navigate("/login?redirect=/lcfpi/submit")} className={BTN_PRIMARY + " text-base px-8 py-4"} data-testid="hero-apply">
              Apply to Join Pipeline <ArrowRight size={16} />
            </button>
            <button onClick={() => navigate("/lcfpi-portal")} className={BTN_DARK + " text-base px-8 py-4"} data-testid="hero-investor">
              Investor & DFI Portal
            </button>
            <a href="/lcfpi/registry" className={BTN_DARK + " text-base px-8 py-4"}>
              Public Project Registry
            </a>
          </div>

          {/* Live stats */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-8" data-testid="hero-stats">
            <p className="text-xs font-bold text-blue-300 uppercase tracking-widest mb-6">Pipeline Performance {statsLoaded && "— Live"}</p>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-6">
              <StatCard value={stats.total_projects||"—"} label="Projects in Pipeline" sub="Active registrations" />
              <StatCard value={fmtU(stats.total_funding_usd)} label="Funding Pipeline" sub="Total requirement" />
              <StatCard value={stats.tier1_ready||"—"} label="Finance Ready (T1)" sub="DFI-eligible projects" />
              <StatCard value={stats.sectors_covered||"—"} label="Sectors Covered" sub="Climate finance focus" />
              <StatCard value={stats.eco_labels_issued||"—"} label="Eco-Labels Issued" sub="QR-verified" />
              <StatCard value={stats.total_tco2e_monitored ? `${(stats.total_tco2e_monitored/1000).toFixed(1)}K` : "—"} label="tCO₂e Monitored" sub="GHG Protocol" />
            </div>
          </div>
        </div>
      </section>

      {/* CLIMATE FINANCING GAP */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">The Climate Financing Gap</span>
              <h2 className="text-3xl font-black text-slate-900 mt-2 mb-4">Lagos needs $4.8B in climate finance by 2030</h2>
              <p className="text-slate-600 mb-4">Lagos State faces a critical gap between climate ambition and bankable, investment-ready climate projects. LCFPI bridges this gap by systematically assessing, rating, and packaging SMEs and projects into DFI-ready investment opportunities.</p>
              <div className="space-y-3">
                {[["$4.8B","Climate finance required by 2030 (Lagos State NDC)"],["78%","Of SMEs lack finance-ready documentation"],["$0","Most climate SMEs have never accessed DFI finance"],["6","LCFPI assessment pillars align to DFI lending criteria"]].map(([v,l])=>(
                  <div key={v} className="flex items-center gap-3">
                    <span className="text-lg font-black text-blue-600 w-14 flex-shrink-0">{v}</span>
                    <span className="text-sm text-slate-500">{l}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">How LCFPI Addresses the Gap</p>
              <div className="space-y-4">
                {[["Assessment","Standardised climate & finance readiness assessment","#3b82f6"],
                  ["Rating","Finance Readiness Score™ + Eco-Label classification","#059669"],
                  ["Packaging","DFI-ready investment profiles and data rooms","#d97706"],
                  ["Matching","Structured introductions to DFIs, banks, and investors","#7c3aed"],
                ].map(([title,desc,color])=>(
                  <div key={title} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{background:color}}/>
                    <div>
                      <p className="text-sm font-bold text-slate-800">{title}</p>
                      <p className="text-xs text-slate-500">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Workflow</span>
            <h2 className="text-3xl font-black text-slate-900 mt-2">How LCFPI Works</h2>
            <p className="text-slate-500 mt-2 max-w-xl mx-auto">A structured 8-step pathway from project registration to institutional financing</p>
          </div>
          <div className="grid grid-cols-2 gap-x-12 gap-y-6">
            {[["1","Register","Submit your organisation profile, financing requirement, and project description",["#1e40af"]],
              ["2","Climate Assessment","Complete Scope 1–3 emissions and circularity inventory using DeCarbonIQ engine",["#059669"]],
              ["3","SDG Mapping","Automated SDG contribution analysis and development impact scoring",["#3b82f6"]],
              ["4","Finance Readiness Score™","7-pillar assessment producing a Tier 1/2/3 classification",["#7c3aed"]],
              ["5","Monetisation Screening","Carbon and plastic credit screening for additional revenue potential",["#d97706"]],
              ["6","PoA Structuring","Programme of Activities matching for scalable credit issuance",["#0891b2"]],
              ["7","Investment Packaging","Auto-generated investment memorandum and institutional data room",["#059669"]],
              ["8","Eco-Label & Registry","QR-verified Lagos State Eco-Label + public registry listing",["#1e40af"]],
            ].map(([num,title,desc,[color]])=>(
              <StepCard key={num} num={num} title={title} desc={desc} color={color} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTOR ECOSYSTEM */}
      <section id="sectors" className="py-20 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Sector Coverage</span>
            <h2 className="text-3xl font-black text-slate-900 mt-2">Climate Finance Across 8 Sectors</h2>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {[["♻️","Waste & Circularity","Plastic recovery, composting, e-waste, recycling infrastructure",["VCM Eligible","Plastic Credits"]],
              ["☀️","Clean Energy","Solar, mini-grids, biogas, energy efficiency, storage",["Green Bond","SDG 7"]],
              ["🏗️","Low Carbon Housing","Affordable housing, green construction, building efficiency",["Social Bond","SDG 11"]],
              ["🌱","Agriculture & Food","Sustainable farming, food processing, supply chain decarbonisation",["VCM","SDG 2"]],
              ["🚌","Clean Mobility","Electric vehicles, CNG fleets, BRT infrastructure",["Green Bond","SDG 11"]],
              ["🏭","Manufacturing","Industrial efficiency, fuel switching, process optimisation",["SLL","SDG 9"]],
              ["💧","Water & Sanitation","Treatment, distribution, wastewater reuse",["DFI Finance","SDG 6"]],
              ["🏥","Healthcare","Clean energy for health facilities, waste management",["Social Bond","SDG 3"]],
            ].map(([icon,name,desc,tags])=>(
              <SectorCard key={name} icon={icon} name={name} desc={desc} tags={tags} />
            ))}
          </div>
        </div>
      </section>

      {/* ECO-LABEL FRAMEWORK */}
      <section id="eco-labels" className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Eco-Label Framework</span>
            <h2 className="text-3xl font-black text-slate-900 mt-2">Lagos State Climate & Circularity Eco-Label</h2>
            <p className="text-slate-500 mt-2">QR-verifiable. DeCarbonIQ certified. Recognised by OCCE Lagos State.</p>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {[["Platinum","#6366f1","Score ≥80, Climate ≥75, Circularity ≥70","Transformational leader"],
              ["Gold","#d97706","Score ≥65, Climate ≥60","Advanced transition"],
              ["Silver","#64748b","Score ≥50","Strong progress"],
              ["Bronze","#b45309","Score ≥35","Emerging transition"],
              ["Participating","#94a3b8","Score <35","Early engagement"],
            ].map(([label,color,criteria,meaning])=>(
              <div key={label} className="text-center rounded-xl border p-5 hover:shadow-md transition-shadow" style={{borderColor:color+"40"}}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{background:color+"20"}}>
                  <Award size={20} style={{color}} />
                </div>
                <p className="text-sm font-black mb-1" style={{color}}>{label}</p>
                <p className="text-xs text-slate-500 mb-2">{meaning}</p>
                <p className="text-xs text-slate-400">{criteria}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTOR INFRASTRUCTURE */}
      <section id="investors" className="py-20 px-6 bg-[#0f172a]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-bold text-blue-400 uppercase tracking-widest">Institutional Infrastructure</span>
            <h2 className="text-3xl font-black text-white mt-2">Built for DFIs, Banks & Institutional Investors</h2>
            <p className="text-slate-400 mt-2 max-w-2xl mx-auto">LCFPI provides the institutional infrastructure that transforms fragmented climate SMEs into a structured, rated, investable pipeline.</p>
          </div>
          <div className="grid grid-cols-3 gap-5">
            {[[TrendingUp,"Finance Readiness Score™","7-pillar assessment aligned to DFI and commercial bank lending criteria. Tier 1/2/3 classification.","#3b82f6"],
              [Award,"Eco-Label Registry","QR-verified Lagos State Eco-Label. Public registry with immutable audit trail. DeCarbonIQ certified.","#059669"],
              [Shield,"Institutional Data Rooms","Secure, structured data rooms for DFI due diligence. Version-controlled. Investor access management.","#7c3aed"],
              [BarChart2,"Portfolio Intelligence","Aggregated portfolio analytics. Sector heatmaps. Climate risk analytics. Impact tracking.","#d97706"],
              [Globe,"SDG Impact Reporting","Standardised SDG contribution reporting aligned to IFC Performance Standards and OECD DAC Rio Markers.","#0891b2"],
              [CheckCircle2,"Verification & MRV","DeCarbonIQ SSOT MRV infrastructure. Evidence vault. GHG Protocol compliant. VVB-ready.","#059669"],
            ].map(([Icon,title,desc,color])=>(
              <div key={title} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors">
                <Icon size={20} style={{color}} className="mb-3"/>
                <p className="text-sm font-bold text-white mb-2">{title}</p>
                <p className="text-xs text-slate-400">{desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button onClick={()=>navigate("/login?redirect=/lcfpi/dashboard")} className={BTN_PRIMARY + " text-base px-8 py-4"} data-testid="investor-portal-btn">
              Access Investor Portal <ExternalLink size={14}/>
            </button>
          </div>
        </div>
      </section>

      {/* APPLY CTA */}
      <section id="apply" className="py-20 px-6 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-black text-white mb-4">Ready to Join the Pipeline?</h2>
          <p className="text-blue-200 mb-8 text-lg">Register your climate project, receive your Finance Readiness Score™, and get matched with DFI and institutional investors.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={()=>navigate("/login?redirect=/lcfpi/submit")} className="bg-white text-blue-700 font-black text-base px-8 py-4 rounded-xl hover:bg-blue-50 transition-colors" data-testid="cta-apply">
              Apply to Join Pipeline <ArrowRight size={16} className="inline ml-1"/>
            </button>
            <button onClick={()=>navigate("/login?redirect=/lcfpi/dashboard")} className={BTN_DARK + " text-base px-8 py-4"}>
              View Impact Dashboard
            </button>
          </div>
          <p className="text-blue-300 text-xs mt-6">
            Powered by DeCarbonIQ · In partnership with OCCE, Lagos State Government<br/>
            programme@lcfpi.lagosstate.gov
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0f172a] py-10 px-6 border-t border-slate-800">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-sm font-black text-white">LCFPI</p>
            <p className="text-xs text-slate-500">Lagos Climate Finance Pipeline Initiative</p>
            <p className="text-xs text-slate-600 mt-1">Powered by DeCarbonIQ · OCCE Lagos State</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">© 2026 DeCarbonIQ. All rights reserved.</p>
            <p className="text-xs text-slate-600">GHG Protocol Aligned · ICMA GBP 2021</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
